/**
 * Competitive gender helpers for mens-doubles eligibility.
 *
 * A female session player flagged as `mensDoublesEligible` is treated as
 * "effectively male" ONLY for:
 *   - mens-doubles pool counting and feasibility gates
 *   - team composition checks under balanced_only
 *   - mens game-type hint units
 *   - ELO balance scoring (uses shadow ELO instead of levels_elo_rating)
 *
 * Physical gender is unchanged for mixed formation, display, and identity.
 */

/**
 * Whether a player is flagged as mens-doubles-eligible this session.
 *
 * @param {string} playerId
 * @param {Object} playerWaitMetrics - { [playerId]: { mensDoublesEligible?: boolean } }
 * @returns {boolean}
 */
export function isMensDoublesEligible(playerId, playerWaitMetrics) {
  return playerWaitMetrics?.[playerId]?.mensDoublesEligible === true;
}

/**
 * Effective gender for mens-doubles context.
 * Returns 'male' for a female player that is mens-doubles-eligible; otherwise
 * returns the player's physical gender.
 *
 * @param {Object} player         - { id, gender }
 * @param {Object} playerWaitMetrics
 * @returns {'male'|'female'|string|null}
 */
export function getEffectiveGenderForMens(player, playerWaitMetrics) {
  if (!player) return null;
  const physical = (player.gender || '').toLowerCase();
  if (physical === 'female' && isMensDoublesEligible(player.id, playerWaitMetrics)) {
    return 'male';
  }
  return physical;
}

/**
 * Whether all players on a team are effectively male (for mens-doubles composition).
 *
 * @param {string[]} teamPlayerIds
 * @param {Object}   playersById
 * @param {Object}   playerWaitMetrics
 * @returns {boolean}
 */
export function isTeamEffectivelyAllMale(teamPlayerIds, playersById, playerWaitMetrics) {
  if (!teamPlayerIds || teamPlayerIds.length === 0) return false;
  return teamPlayerIds.every((id) => {
    const player = playersById?.[id];
    return getEffectiveGenderForMens(player, playerWaitMetrics) === 'male';
  });
}

/**
 * Whether a team has mixed physical genders (for mixed-doubles formation).
 * Always uses physical gender — eligibility never converts for mixed purposes.
 *
 * @param {string[]} teamPlayerIds
 * @param {Object}   playersById
 * @returns {boolean}
 */
export function isTeamPhysicallyMixed(teamPlayerIds, playersById) {
  if (!teamPlayerIds || teamPlayerIds.length < 2) return false;
  const genders = teamPlayerIds.map((id) =>
    (playersById?.[id]?.gender || '').toLowerCase()
  );
  return genders.includes('male') && genders.includes('female');
}

/**
 * Count effective males in a player id array.
 * Eligible females count as male; non-eligible females do not.
 *
 * @param {string[]} playerIds
 * @param {Object}   playersById
 * @param {Object}   playerWaitMetrics
 * @returns {number}
 */
export function countEffectiveMales(playerIds, playersById, playerWaitMetrics) {
  if (!playerIds) return 0;
  return playerIds.filter(
    (id) => getEffectiveGenderForMens(playersById?.[id], playerWaitMetrics) === 'male'
  ).length;
}

/**
 * Count physical females that are NOT mens-doubles-eligible.
 * Used for womens-doubles feasibility.
 *
 * @param {string[]} playerIds
 * @param {Object}   playersById
 * @param {Object}   playerWaitMetrics
 * @returns {number}
 */
export function countWomensEligibleFemales(playerIds, playersById, playerWaitMetrics) {
  if (!playerIds) return 0;
  return playerIds.filter((id) => {
    const g = (playersById?.[id]?.gender || '').toLowerCase();
    return g === 'female' && !isMensDoublesEligible(id, playerWaitMetrics);
  }).length;
}

/**
 * Count physical males (unaffected by eligibility flag).
 *
 * @param {string[]} playerIds
 * @param {Object}   playersById
 * @returns {number}
 */
export function countPhysicalMales(playerIds, playersById) {
  if (!playerIds) return 0;
  return playerIds.filter(
    (id) => (playersById?.[id]?.gender || '').toLowerCase() === 'male'
  ).length;
}

/**
 * Count physical females (all females, regardless of eligibility).
 *
 * @param {string[]} playerIds
 * @param {Object}   playersById
 * @returns {number}
 */
export function countPhysicalFemales(playerIds, playersById) {
  if (!playerIds) return 0;
  return playerIds.filter(
    (id) => (playersById?.[id]?.gender || '').toLowerCase() === 'female'
  ).length;
}

/**
 * Count physical females flagged as mens-doubles-eligible.
 *
 * @param {string[]} playerIds
 * @param {Object}   playersById
 * @param {Object}   playerWaitMetrics
 * @returns {number}
 */
export function countMensDoublesEligibleFemales(playerIds, playersById, playerWaitMetrics) {
  if (!playerIds) return 0;
  return playerIds.filter((id) => {
    const g = (playersById?.[id]?.gender || '').toLowerCase();
    return g === 'female' && isMensDoublesEligible(id, playerWaitMetrics);
  }).length;
}

/**
 * Whether a lineup (four player ids) contains at least one mens-doubles-eligible female.
 *
 * @param {string[]} allPlayerIds - all four players in the game
 * @param {Object}   playerWaitMetrics
 * @param {Object}   playersById
 * @returns {boolean}
 */
export function lineupIncludesMensDoublesEligibleFemale(allPlayerIds, playerWaitMetrics, playersById) {
  return countMensDoublesEligibleFemales(allPlayerIds, playersById, playerWaitMetrics) > 0;
}

/**
 * Whether every player on a team is physically female.
 *
 * @param {string[]} teamPlayerIds
 * @param {Object}   playersById
 * @returns {boolean}
 */
export function isTeamAllPhysicalFemale(teamPlayerIds, playersById) {
  if (!teamPlayerIds || teamPlayerIds.length === 0) return false;
  return teamPlayerIds.every(
    (id) => (playersById?.[id]?.gender || '').toLowerCase() === 'female',
  );
}

/**
 * Whether a lineup should be treated as session mens doubles for ELO exemption / UI badge / scoring.
 * Host-endorsed: exactly 3 physical males + 1 mens-doubles-eligible female, split 2M vs M+eligible F
 * (both teams effectively all-male). Standard mixed, womens, pure mens, and multi-eligible-F lineups
 * stay on normal rules.
 *
 * @param {string[]} team1PlayerIds
 * @param {string[]} team2PlayerIds
 * @param {Object}   playerWaitMetrics
 * @param {Object}   playersById
 * @returns {boolean}
 */
export function isSessionMensDoublesEloExemptLineup(
  team1PlayerIds,
  team2PlayerIds,
  playerWaitMetrics,
  playersById,
) {
  const allIds = [...(team1PlayerIds || []), ...(team2PlayerIds || [])];
  if (allIds.length !== 4 || !playerWaitMetrics) {
    return false;
  }
  if (countPhysicalMales(allIds, playersById) !== 3) {
    return false;
  }
  if (countMensDoublesEligibleFemales(allIds, playersById, playerWaitMetrics) !== 1) {
    return false;
  }
  if (countWomensEligibleFemales(allIds, playersById, playerWaitMetrics) > 0) {
    return false;
  }
  return (
    isTeamEffectivelyAllMale(team1PlayerIds, playersById, playerWaitMetrics) &&
    isTeamEffectivelyAllMale(team2PlayerIds, playersById, playerWaitMetrics)
  );
}

/**
 * ELO to use for matchmaking balance calculations.
 * For mens-doubles-eligible females, returns their session shadow ELO (if set);
 * falls back to levels_elo_rating. For all other players returns levels_elo_rating.
 *
 * @param {string} playerId
 * @param {Object} playersById
 * @param {Object} playerWaitMetrics
 * @returns {number}
 */
export function getLevelsEloForMatchmaking(playerId, playersById, playerWaitMetrics) {
  const player = playersById?.[playerId];
  const DEFAULT_ELO = 1200;
  if (!player) return DEFAULT_ELO;
  if (isMensDoublesEligible(playerId, playerWaitMetrics)) {
    const shadow = playerWaitMetrics?.[playerId]?.mensDoublesShadowElo;
    if (shadow != null && Number.isFinite(Number(shadow))) {
      return Number(shadow);
    }
  }
  return player.levels_elo_rating ?? DEFAULT_ELO;
}
